import React, { useCallback, useState } from 'react';

import { Select } from '@aline/common';
import type { UUID } from '@aline/common';
import { isEmpty } from 'ramda';
import { AssignRoleList } from 'src/components/AssignRoleList';
import type { RoleAssignmentMap } from 'src/components/AssignRoleList';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineDialog } from 'src/components/generic/AlineDialog';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

interface AssignRolesDialogProps {
  onClose: () => void;
  open: boolean;
  title: string;
  workflowId: UUID;
  /** Only these role placeholders by permission id, such as the ones on one task. Every unfilled placeholder when omitted. */
  permissionIds?: readonly UUID[];
}

const useStyles = makeStyles()(theme => ({
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 0, 0, 0),
  },
  dialogPaper: {
    width: theme.dialogStyles.mediumDialogWidth,
  },
}));

/**
 * Fill a workflow's unfilled roles with people. Each role is filled on its permission, so every
 * task and signature step that names the role picks up the person.
 */
export const AssignRolesDialog = React.memo(function AssignRolesDialog({
  onClose,
  open,
  permissionIds,
  title,
  workflowId,
}: AssignRolesDialogProps) {
  const { classes } = useStyles();
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [assignmentMap, setAssignmentMap] = useState<RoleAssignmentMap>({});
  const [changeCollaboratorAssignment] = Apollo.useChangeCollaboratorAssignmentMutation();
  const dispatchAssignFailed = useDispatchNotification({
    message: 'Could not assign every role. Nothing was changed for the ones that failed.',
    variant: 'error',
  });

  const handleClose = useCallback(() => {
    setAssignmentMap({});
    onClose();
  }, [onClose]);

  /**
   * A rejection has to be caught here. Without it the dialog keeps a spinning Assign button and
   * never closes, because neither the reset nor the close below would run: a placeholder removed
   * in another tab, or a label the model rejects, is a plausible failure rather than a bug.
   */
  const handleAssign = useCallback(async () => {
    setSubmitting(true);
    try {
      await Promise.all(
        Object.entries(assignmentMap).map(([permissionId, { email, userId: assignedUserId }]) =>
          changeCollaboratorAssignment({
            variables: { input: { assignedUserId, email, permissionId } },
          })
        )
      );
      handleClose();
    } catch {
      dispatchAssignFailed();
    } finally {
      setSubmitting(false);
    }
  }, [assignmentMap, changeCollaboratorAssignment, dispatchAssignFailed, handleClose]);

  return (
    <AlineDialog
      classes={{ paper: classes.dialogPaper }}
      data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.container}
      open={open}
      title={title}
      onClose={handleClose}
    >
      <AssignRoleList
        entityId={workflowId}
        entityType="Workflow"
        filterBy="unassigned"
        permissionIds={permissionIds}
        onListChange={setAssignmentMap}
      />
      <div className={classes.buttonRow}>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.cancelButton}
          disabled={isSubmitting}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.nextButton}
          disabled={isEmpty(assignmentMap)}
          loading={isSubmitting}
          variant="contained"
          onClick={handleAssign}
        >
          Assign
        </AlineButton>
      </div>
    </AlineDialog>
  );
});
